import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { FACTION_SPECS, MATCH_SIZES, PHASE_SECONDS, POCKET_MAX, WAR_PHASE_DURATIONS, loreLine, regionName } from '@tb/shared';
import type { BotSkill, Faction, WarSummary } from '@tb/shared';
import { HolographicCity } from '../HolographicCity';
import { serverRemaining } from '../net';
import type { Net } from '../net';
import { LoreOpening } from './LoreOpening';
import { SettingsDialog } from './SettingsDialog';
import { useChosenSide } from './side';
import { CLOSED_GATE, useTesterEntry, useWarEntry } from './WarGate';
import { refusalNotice, warCall } from './war';
import { Badge, Choice, Choices, Dialog, Icon, Notice } from '../ui/kit';
import tradingBlowsLogo from './trading-blows.svg';

type FrameProps = {
  children: ReactNode;
  /** Command training, also offered at the end of the optional story. */
  onTrain?: () => void;
  name?: string;
  state: Net['state'];
  onChangeName?: () => void;
  war?: WarSummary | null;
};

/** One static image is selected by the browser; no city renderer runs here. */
function Artwork() {
  return <picture className="front-art" aria-hidden="true">
    <source media="(orientation: portrait)" type="image/avif" srcSet="/lobby/mobile-480.avif 480w, /lobby/mobile-853.avif 853w" sizes="100vw" />
    <source media="(orientation: portrait)" type="image/webp" srcSet="/lobby/mobile-480.webp 480w, /lobby/mobile-853.webp 853w" sizes="100vw" />
    <source media="(max-aspect-ratio: 3/2)" type="image/avif" srcSet="/lobby/tablet-1024.avif 1024w, /lobby/tablet-1448.avif 1448w" sizes="100vw" />
    <source media="(max-aspect-ratio: 3/2)" type="image/webp" srcSet="/lobby/tablet-1024.webp 1024w, /lobby/tablet-1448.webp 1448w" sizes="100vw" />
    <source type="image/avif" srcSet="/lobby/desktop-1280.avif 1280w, /lobby/desktop-1672.avif 1672w" sizes="100vw" />
    <img src="/lobby/desktop-1280.webp" srcSet="/lobby/desktop-1280.webp 1280w, /lobby/desktop-1672.webp 1672w" sizes="100vw" alt="" decoding="async" fetchPriority="high" width="1672" height="941" />
  </picture>;
}

function Regions({ war }: { war: WarSummary }) {
  // After regrouping these regions describe the original mobilisation, not current geography.
  // A battle is one cluster: there is nothing to mobilise.
  if (war.battle || war.state === 'LOCKED_ENDGAME' || war.state === 'COMPLETE') return null;
  return <div className="front-regions glass" aria-label={`${war.activeRegions} of ${war.regions} regions mobilised`}>
    <div className="front-region-markers" aria-hidden="true">{Array.from({ length: war.regions }, (_, i) => <span key={i} className={i < war.activeRegions ? 'active' : ''}>{regionName(i)}</span>)}</div>
    <span>{war.activeRegions} / {war.regions} regions mobilised</span>
  </div>;
}

export function FrontFrame({ children, name, state, onChangeName, war, onTrain }: FrameProps) {
  const [settings, setSettings] = useState(false);
  const [story, setStory] = useState(false);
  const connection = state === 'open' ? 'Connected' : state === 'closed' ? 'Reconnecting…' : state === 'superseded' ? 'Open in another tab' : 'Connecting…';
  return <div className="front" data-testid="front">
    <Artwork />
    <header className="front-utilities">
      <span className="front-connection" role="status"><Badge tone={state === 'open' ? 'ok' : 'warn'} aria-hidden="true" />{connection}</span>
      {name && <span className="front-identity" title={name}>{name}</span>}
      {onChangeName && <button className="btn sm ghost" onClick={onChangeName}>Change name</button>}
      <button className="btn sm ghost" onClick={() => setSettings(true)} aria-haspopup="dialog">Settings</button>
    </header>
    <main className="front-main">
      <div className="front-brand">
        <span className="front-kicker">Strategic command</span>
        <h1 className="front-title"><img className="front-logo" src={tradingBlowsLogo} alt="Trading Blows" width={803} height={388} /></h1>
        <p className="front-tagline">Two sides of one country. Both still call it home.</p>
        <button className="btn link front-story" onClick={() => setStory(true)} aria-haspopup="dialog" data-testid="lore-open">Read the story <Icon name="arrow-right" /></button>
      </div>
      <section className="front-plate housing" aria-labelledby="front-heading">{children}</section>
      {war && <Regions war={war} />}
    </main>
    <footer className="front-briefing">
      <ol className="front-phases" aria-label="Each round: both sides plan, attack and defend, then automatic resolution and replays">
        <li><span className="front-phase-n" aria-hidden="true">01</span><b>Plan</b><span>{WAR_PHASE_DURATIONS.PLAN}s</span></li>
        <li><span className="front-phase-n" aria-hidden="true">02</span><b>Attack</b><span>{WAR_PHASE_DURATIONS.ATTACK}s</span></li>
        <li><span className="front-phase-n" aria-hidden="true">03</span><b>Defence</b><span>{WAR_PHASE_DURATIONS.DEFEND}s</span></li>
      </ol>
      {/* What the server records about play (D36), said where every visitor sees it. */}
      <p className="front-privacy" data-testid="front-privacy">Anonymous play statistics are recorded to improve the game: no names, no addresses.</p>
    </footer>
    {settings && <SettingsDialog identity close={() => setSettings(false)} />}
    {story && <LoreOpening close={() => setStory(false)} onTrain={onTrain ? () => { setStory(false); onTrain(); } : undefined} />}
  </div>;
}

export function FrontName({ name, setName, onEnter }: { name: string; setName: (n: string) => void; onEnter: () => void }) {
  return <>
    <h2 id="front-heading">Take command</h2>
    <form className="front-name" onSubmit={(event) => { event.preventDefault(); onEnter(); }}>
      <CommanderName name={name} setName={setName} />
      <button className="btn primary front-cta" type="submit">Take command <Icon name="arrow-right" /></button>
    </form>
  </>;
}

function CommanderName({ name, setName }: { name: string; setName: (n: string) => void }) {
  return <>
    <label htmlFor="front-name">Commander name</label>
    <input id="front-name" type="text" placeholder="Your name" value={name} maxLength={24} autoComplete="nickname" autoCapitalize="words" spellCheck={false} onChange={(event) => setName(event.target.value)} />
  </>;
}

function useTick(active: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setTick((value) => value + 1), 250);
    return () => clearInterval(timer);
  }, [active]);
}

/** The `?e2e` page's room clock: the browser walkthroughs ask for a short one with `&phase=`. */
function testRoom(): { phase: number } | null {
  const query = new URLSearchParams(location.search);
  if (!query.has('e2e')) return null;
  const asked = Number(query.get('phase'));
  return { phase: Number.isFinite(asked) && asked >= 5 ? asked : PHASE_SECONDS };
}

export function FrontLobby({ net, name, setName, identified, preparing, prepareEntry }: {
  net: Net; name: string; setName: (n: string) => void; identified: boolean;
  preparing: boolean; prepareEntry: (action: () => void) => void;
}) {
  const [pressed, setPressed] = useState(false);
  const [side, setSide] = useChosenSide();
  const war = net.wars[0] ?? null;
  const connected = net.state === 'open';
  useTick(war?.state === 'LAUNCHING');
  const call = warCall(war, war ? Math.ceil(serverRemaining(war.launchEndsAt, war.serverNow, war.receivedAt) / 1000) : 0);
  // The war takes a commander on the side they chose while any of its cities
  // there is an NPC's; only a side every one of whose cities is a human's is shut.
  const sideShut = side != null && war != null && war.admissions && !war.sides[side].open;
  const test = testRoom();
  useEffect(() => {
    if (!pressed) return;
    const timer = setTimeout(() => setPressed(false), 4000);
    return () => clearTimeout(timer);
  }, [pressed]);
  useEffect(() => { setPressed(false); }, [net.errors.length, net.refusal, net.state]);
  const entry = useWarEntry(net, pressed || preparing, () => setPressed(true));
  // Training and battles, while the game is closed to the public (D36): a
  // tester's word goes with the press, and a newcomer is asked for it.
  const closed = !net.access.open;
  const access = useTesterEntry(net, 'GAME_CLOSED', CLOSED_GATE, pressed || preparing);
  const train = () => { setPressed(true); prepareEntry(() => access.ask((password) => startTraining(net, password))); };
  // For the browser walkthroughs only (scripts/ui): a room of their own on a
  // short clock, opened through a page hook rather than a button. The live
  // server refuses it (index.ts).
  useEffect(() => {
    if (!test) return;
    const w = window as unknown as { __tbE2E?: Record<string, unknown> };
    w.__tbE2E = { ...(w.__tbE2E ?? {}), openTestRoom: () => {
      if (!connected || preparing || pressed) return false;
      prepareEntry(() => net.createMatch(MATCH_SIZES[0], test.phase));
      return true;
    } };
  });
  return <>
    <h2 id="front-heading">Take command</h2>
    {closed && <Notice tone="warn" inline data-testid="access-closed">Trading Blows is closed to the public for now. Testers can still play.</Notice>}
    <SidePicker side={side} setSide={setSide} />
    {/* Training first, the way in for a newcomer: a name, if there is none yet, goes with it and Enter presses it. */}
    <form className="front-name" onSubmit={event => { event.preventDefault(); if (connected && !pressed && !preparing) train(); }}>
      {!identified && <CommanderName name={name} setName={setName} />}
      <button className="btn primary front-cta" type="submit" data-testid="train" disabled={!connected || pressed || preparing}>
        {pressed || preparing ? 'Connecting…' : 'Command training'}<Icon name="arrow-right" />
      </button>
    </form>
    <Battles net={net} side={side} busy={!connected || pressed || preparing}
      enter={(go) => { setPressed(true); prepareEntry(() => access.ask(go)); }} />
    {/* The full-scale war last, on its own card: open only to testers while
        the game is under development. Its state is written beneath only while
        there is a war. */}
    <section className="front-subcard front-war-status recessed" aria-labelledby="front-war-heading">
      <h3 className="front-kicker" id="front-war-heading">{WAR_SIZE}v{WAR_SIZE} battle</h3>
      <button className="btn front-train" type="button" data-testid="play" disabled={!connected || !call.open || sideShut || pressed || preparing}
        onClick={() => prepareEntry(entry.enter)} aria-describedby={war ? 'front-entry-detail' : undefined}>
        {call.cta}<Icon name="arrow-right" />
      </button>
      {war && <span className="front-war-state" role="status">{call.heading}</span>}
      {war && <p className="front-strength">{war.humans} human {war.humans === 1 ? 'commander' : 'commanders'}{war.seats > 0 && <> · {war.seats} cities</>}</p>}
      {war && <p className="front-description" id="front-entry-detail">{sideShut
        ? `Every ${FACTION_SPECS[side!].name} city has a human commander. Choose the ${FACTION_SPECS[side === 'WEST' ? 'EAST' : 'WEST'].name}, or either side.`
        : call.detail}</p>}
    </section>
    {connected && net.refusal && net.refusal.code !== 'WAR_PASSWORD' && !(net.refusal.code === 'GAME_CLOSED' && access.asking) && <Notice tone="warn" inline role="alert" data-testid="war-refusal">{refusalNotice(net.refusal)}</Notice>}
    {!connected && <Notice tone="warn" inline>{net.state === 'superseded' ? 'Use “Play here instead” to reconnect this tab.' : 'Waiting for the server…'}</Notice>}
    {entry.gate}
    {access.gate}
  </>;
}

/**
 * The side this device fights for (docs/SPRINT.md item 4), for the war, both
 * battles and training: two equal cards, each its city as the theatre draws
 * it beside its name, and its creed, with Either side beneath under its name
 * alone. The creeds are the only words under a choice the lobby keeps (owner,
 * 25 October 2026). Neither is presented as the game's own
 * (docs/world/ETHICAL_DESIGN.md).
 */
function SidePicker({ side, setSide }: { side: Faction | null; setSide: (side: Faction | null) => void }) {
  return <Choices label="Your side" className="front-sides" data-testid="side-picker">
    {SIDES.map((f) => (
      <Choice key={f} on={side === f} className={`front-side ${f === 'WEST' ? 'frontier' : 'heritage'}`} data-testid={`side-${f}`}
        mark={`var(--map-${f === 'WEST' ? 'frontier' : 'heritage'})`} onClick={() => setSide(f)}
        art={<span className="front-side-art"><HolographicCity faction={f} /></span>}
        name={FACTION_SPECS[f].title} meta={FACTION_SPECS[f].creed} />
    ))}
    <Choice on={side == null} size="wide" className="front-side-either" data-testid="side-either" onClick={() => setSide(null)} name="Either side" />
  </Choices>;
}

const SIDES: readonly Faction[] = ['WEST', 'EAST'];

/** Cities a side in the full-scale war, for its card's title. */
const WAR_SIZE = MATCH_SIZES[MATCH_SIZES.length - 1];

/** The NPC skills a battle can be fought against: the three a commander knows by name. */
const BATTLE_SKILLS: readonly { value: BotSkill; label: string }[] = [
  { value: 'RECRUIT', label: 'Recruit' },
  { value: 'REGULAR', label: 'Regular' },
  { value: 'VETERAN', label: 'Veteran' },
];

/**
 * Battles (MatchConfig.cluster): one 4v4 cluster on its own, open to everyone,
 * the two side by side on a card of their own. Against NPCs a dialog asks their skill and the battle
 * starts on the answer; against other commanders it joins the open call-up,
 * whose places and seconds read beneath while it lasts. Nothing else is
 * written under them (owner, 25 October 2026).
 */
function Battles({ net, side, busy, enter }: { net: Net; side: Faction | null; busy: boolean; enter: (go: (password?: string) => void) => void }) {
  const [asking, setAsking] = useState(false);
  const call = net.battles[0] ?? null;
  useTick(call != null);
  // Each side's places in the open call-up; the side chosen, when it is full,
  // is shut until the next battle (owner, 20 October 2026).
  const full = side != null && call != null && !call.sides[side].open;
  const places = call
    ? `${SIDES.map((f) => `${FACTION_SPECS[f].name} ${call.sides[f].humans}/${POCKET_MAX}`).join(' · ')} · ${Math.ceil(serverRemaining(call.launchEndsAt, call.serverNow, call.receivedAt) / 1000)}s`
    : null;
  const status = full
    ? `The ${FACTION_SPECS[side!].name}'s places are taken: choose the ${FACTION_SPECS[side === 'WEST' ? 'EAST' : 'WEST'].name}, or wait for the next battle. ${places}`
    : places;
  const versus = (skill: BotSkill) => { setAsking(false); enter((password) => net.battle('NPC', skill, password)); };
  return <section className="front-subcard front-battle recessed" aria-labelledby="front-battle-heading">
    <h3 className="front-kicker" id="front-battle-heading">{POCKET_MAX}v{POCKET_MAX} battles</h3>
    <div className="front-battle-pair">
      <button className="btn front-train" type="button" data-testid="battle-npc" disabled={busy} onClick={() => setAsking(true)} aria-haspopup="dialog">
        Battle vs NPCs<Icon name="arrow-right" />
      </button>
      <button className="btn front-train" type="button" data-testid="battle-players" disabled={busy || full} onClick={() => enter((password) => net.battle('PLAYERS', undefined, password))}
        aria-describedby={status ? 'front-battle-status' : undefined}>
        Battle vs commanders<Icon name="arrow-right" />
      </button>
    </div>
    {status && <p className="front-train-detail" id="front-battle-status" role="status">{status}</p>}
    {asking && <Dialog size="sm" material="housing" className="front-skill" onClose={() => setAsking(false)}
      kicker={`One ${POCKET_MAX}v${POCKET_MAX} cluster · no ICBMs`} title="Battle vs NPCs" data-testid="battle-skill" aria-describedby="front-skill-why"
      aside={<button className="btn sm ghost icon" onClick={() => setAsking(false)} aria-label="Close"><Icon name="close" /></button>}>
      <p id="front-skill-why">You and {POCKET_MAX - 1} NPC allies against {POCKET_MAX}. Choose their skill.</p>
      <div className="front-skill-choices">
        {BATTLE_SKILLS.map(({ value, label }) => (
          <button key={value} className="btn front-train" type="button" data-testid={`battle-skill-${value}`} onClick={() => versus(value)}>
            {label}<Icon name="arrow-right" />
          </button>
        ))}
      </div>
    </Dialog>}
  </section>;
}

/**
 * Into command training (docs/interface/TRAINING.md). The city's models start loading on the
 * click, not when the board arrives: training is where a first visit pays for
 * them, and the war after it finds them already in the page.
 */
export function startTraining(net: Net, password?: string): void {
  void import('../render3d/engine/warmUp').then((m) => m.warmUp())
    .catch((error) => console.warn('City preparation will retry on demand.', error));
  net.train(password);
}

function clock(ms: number) {
  const seconds = Math.ceil(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Waiting to be dealt in: the war's call-up or next region, or a battle's call-up. */
export function FrontDeployment({ net }: { net: Net }) {
  return net.deployment!.war.battle ? <BattleDeployment net={net} /> : <WarDeployment net={net} />;
}

function WarDeployment({ net }: { net: Net }) {
  const deployment = net.deployment!;
  const war = deployment.war;
  const launching = war.state === 'LAUNCHING';
  const connected = net.state === 'open';
  useTick(connected && (launching || war.phaseEndsAt > 0));
  return <>
    <span className="front-kicker" role="status">{launching ? 'Call-up' : 'Deployment pending'}</span>
    <h2 id="front-heading">{launching
      ? <>Deployment in <span className="front-countdown">{Math.ceil(serverRemaining(war.launchEndsAt, war.serverNow, deployment.receivedAt) / 1000)}</span></>
      : `Region ${regionName(deployment.region)}`}</h2>
    <p className="front-strength">{FACTION_SPECS[deployment.faction].title}</p>
    <p className="front-description">{launching
      ? 'Region A · Automatic deployment'
      : `Deploys at round ${deployment.region + 1}. Plan and defend before incoming strikes.`}</p>
    {!launching && <p className="front-wait-clock">Round {war.round} / {war.phase} · {war.phaseEndsAt
      ? `${clock(serverRemaining(war.phaseEndsAt, war.serverNow, deployment.receivedAt))} remaining`
      : 'HOLD — watching the last exchange'}</p>}
    <button className="btn link" onClick={net.leaveMatch} disabled={!connected}>Leave call-up</button>
  </>;
}

/** In a battle's call-up against other commanders: seats are dealt, and sealed, when it ends. */
function BattleDeployment({ net }: { net: Net }) {
  const deployment = net.deployment!;
  const war = deployment.war;
  const connected = net.state === 'open';
  useTick(connected);
  return <>
    <span className="front-kicker" role="status">Battle call-up</span>
    <h2 id="front-heading">Battle in <span className="front-countdown">{Math.ceil(serverRemaining(war.launchEndsAt, war.serverNow, deployment.receivedAt) / 1000)}</span></h2>
    <p className="front-strength">{FACTION_SPECS[deployment.faction].title}</p>
    <p className="front-description">{war.humans} of {POCKET_MAX * 2} commanders · one {POCKET_MAX}v{POCKET_MAX} cluster. NPCs take the seats left when it starts, and nobody joins after.</p>
    <button className="btn link" onClick={net.leaveMatch} disabled={!connected}>Leave call-up</button>
  </>;
}

export function FrontLoading({ state, name }: Pick<FrameProps, 'state' | 'name'>) {
  return <FrontFrame state={state} name={name}>
    <span className="front-kicker">Deployment confirmed</span>
    <h2 id="front-heading">Preparing command</h2>
    <p className="front-description" role="status">Opening your city and the theatre…</p>
    <p className="front-lore">{loreLine(new Date().getMinutes())}</p>
  </FrontFrame>;
}
